import React, { useEffect, useState } from 'react';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
} from '@chakra-ui/react';
import { useController, useFormContext } from 'react-hook-form';

import { UF_SELECT_OPTIONS } from '../../../../../constants/ufs';
import { ORGAO_COM_UF_MAX, juntarOrgaoEUf, separarOrgaoEUf } from '../orgaoEmissor';

interface OrgaoEmissorInputProps {
  name: string;
  label?: string;
  isRequired?: boolean;
  isReadOnly?: boolean;
  bg?: string;
  controlMb?: number;
}

/**
 * Orgao emissor do RG + UF de emissao, gravados juntos no campo `name` ("SSP/GO").
 * Em leitura (socio que continua na alteracao) mostra o valor lido, como antes.
 */
export const OrgaoEmissorInput = ({
  name,
  label = 'Órgão emissor',
  isRequired,
  isReadOnly,
  bg,
  controlMb,
}: OrgaoEmissorInputProps) => {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ name, control });
  // As duas partes ficam aqui enquanto o operador digita: separar o valor a cada tecla
  // comeria o espaco entre as palavras do orgao.
  const [partes, setPartes] = useState(() => separarOrgaoEUf(field.value));
  useEffect(() => {
    // Valor que mudou por fora (leitura da CNH, reabrir o rascunho): separa de novo. So'
    // troca se as partes mudarem: "SSP GO" vira SSP + GO e nao volta a ser igual ao campo.
    if (juntarOrgaoEUf(partes.orgao, partes.uf) === (field.value ?? '')) return;
    const novas = separarOrgaoEUf(field.value);
    if (novas.orgao !== partes.orgao || novas.uf !== partes.uf) setPartes(novas);
  }, [field.value, partes]);

  const mudar = (orgao: string, uf: string) => {
    setPartes({ orgao, uf });
    field.onChange(juntarOrgaoEUf(orgao, uf));
  };
  const erro = fieldState.error?.message;

  return (
    <FormControl isRequired={isRequired} isInvalid={!!erro} mb={controlMb ?? 5}>
      <FormLabel fontWeight="bold" mb={1} mr={3} htmlFor={`${name}.orgao`}>
        {label}
      </FormLabel>
      {isReadOnly ? (
        <Input id={`${name}.orgao`} value={field.value ?? ''} isReadOnly bg={bg} />
      ) : (
        <SimpleGrid templateColumns="1fr 6.5rem" gap={2}>
          <Input
            id={`${name}.orgao`}
            placeholder="Ex.: SSP"
            // O limite vale para o orgao com a UF: sobram 3 para a "/UF".
            maxLength={ORGAO_COM_UF_MAX - 3}
            value={partes.orgao}
            onChange={e => mudar(e.target.value, partes.uf)}
            onBlur={() => {
              // "SSP GO" digitado junto: ao sair do campo, a UF vai para a lista.
              const separadas = separarOrgaoEUf(field.value);
              if (separadas.uf && !partes.uf) mudar(separadas.orgao, separadas.uf);
              field.onBlur();
            }}
          />
          <Select
            aria-label="UF de emissão"
            placeholder="UF"
            value={partes.uf}
            onChange={e => mudar(partes.orgao, e.target.value)}
            onBlur={field.onBlur}
          >
            {UF_SELECT_OPTIONS.map(opcao => (
              <option key={opcao.value} value={opcao.value}>
                {opcao.value}
              </option>
            ))}
          </Select>
        </SimpleGrid>
      )}
      {erro && <FormErrorMessage>{erro}</FormErrorMessage>}
    </FormControl>
  );
};
